import { useCallback, useEffect, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { signOut } from '../../lib/adminAuth';

/**
 * Minutos de inactividad tras los que se cierra la sesión administrativa
 * automáticamente. Requisito de seguridad del responsable del panel.
 */
export const INACTIVITY_TIMEOUT_MINUTES = 30;

const INACTIVITY_TIMEOUT_MS = INACTIVITY_TIMEOUT_MINUTES * 60 * 1000;
/** Cuánto antes de expulsar se avisa con el toast. */
const WARNING_LEAD_MS = 60 * 1000;
/**
 * Throttle de los eventos de actividad: reiniciar el temporizador en
 * cada `scroll`/`keydown` sería tanto innecesario como costoso, así que
 * como mucho se reinicia una vez cada `ACTIVITY_THROTTLE_MS`.
 */
const ACTIVITY_THROTTLE_MS = 5000;

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];

/**
 * Cierra la sesión tras `INACTIVITY_TIMEOUT_MINUTES` sin actividad real
 * del usuario, avisando con un Toast un minuto antes. `onExpire` se
 * invoca después de `signOut()` (típicamente para navegar a
 * `/admin/login`); `ProtectedAdminRoute` solo comprueba la sesión al
 * montar, así que sin este callback nadie redirige tras expulsar.
 *
 * No usa estado de React para el temporizador (todo vive en refs), así
 * que reiniciarlo con la actividad del usuario nunca provoca un render.
 */
export default function useInactivityLogout(onExpire) {
  const toast = useToast();
  const warningTimer = useRef(null);
  const logoutTimer = useRef(null);
  const lastResetAt = useRef(0);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  const clearTimers = useCallback(() => {
    if (warningTimer.current) clearTimeout(warningTimer.current);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
  }, []);

  const scheduleTimers = useCallback(() => {
    clearTimers();

    warningTimer.current = setTimeout(() => {
      toast({
        title: 'Tu sesión está por expirar',
        description: 'Por inactividad, se cerrará en 1 minuto.',
        status: 'warning',
        duration: WARNING_LEAD_MS,
        isClosable: true,
      });
    }, INACTIVITY_TIMEOUT_MS - WARNING_LEAD_MS);

    logoutTimer.current = setTimeout(async () => {
      await signOut();
      onExpireRef.current?.();
    }, INACTIVITY_TIMEOUT_MS);
  }, [clearTimers, toast]);

  useEffect(() => {
    scheduleTimers();

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastResetAt.current < ACTIVITY_THROTTLE_MS) return;
      lastResetAt.current = now;
      scheduleTimers();
    };

    ACTIVITY_EVENTS.forEach(eventName =>
      window.addEventListener(eventName, handleActivity, { passive: true })
    );

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach(eventName =>
        window.removeEventListener(eventName, handleActivity)
      );
    };
  }, [scheduleTimers, clearTimers]);
}
